import { Permission } from '@bookora/shared';
import { Navigate, type RouteObject } from 'react-router-dom';

import { RequirePermission } from '@/app/require-permission';
import { RouteErrorBoundary } from '@/components/route-error-boundary';

/**
 * Deep links into the calendar's tabs.
 *
 * The four tabs are ONE screen sharing one payload and one invalidation, so
 * they are not four routes with four data loads. But «صف درخواست‌های آنلاین»
 * is a place a manager bookmarks and a staff notification links to, and
 * `/calendar?tab=requests` is not a URL anyone types. These redirects give each
 * tab a short address that resolves to the same page with the tab preselected —
 * `useCalendarUrlState` reads `?tab=` and nothing else changes.
 *
 * A redirect rather than a second mount of `calendar-page`: two routes
 * rendering the same lazy component would give the tab strip two identities and
 * break the back button between them.
 *
 * `<RequirePermission>` is repeated here because the section wrapper in
 * `router.tsx` covers `/calendar` only; a sibling path is a separate route and
 * inherits nothing from it. Every endpoint behind the screen refuses
 * independently — this is an affordance, as always.
 *
 * ── WIRING (for whoever integrates the sections) ────────────────────────────
 *
 *   import { routes as calendarRoutes } from '@/features/calendar/routes';
 *   const EXTRA_ROUTES: RouteObject[] = [...calendarRoutes];
 *
 * and in `sections.ts`, flip the `calendar` entry to `status: 'ready'` with
 * `load: () => import('@/features/calendar/calendar-page')`.
 */

function redirectToTab(tab: string): React.ReactElement {
  return (
    <RequirePermission anyOf={[Permission.APPOINTMENT_VIEW]}>
      <Navigate to={`/calendar?tab=${tab}`} replace />
    </RequirePermission>
  );
}

// `errorElement` on each LEAF: a throw with no boundary of its own bubbles to
// Root and replaces the whole application rather than the screen that broke.
export const routes: RouteObject[] = [
  {
    path: 'calendar/requests',
    element: redirectToTab('requests'),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'calendar/waitlist',
    element: redirectToTab('waitlist'),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'calendar/schedule',
    element: redirectToTab('schedule'),
    errorElement: <RouteErrorBoundary />,
  },
];
